import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles, TrendingDown } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    id: "free", name: "Starter", price: { m: 0, y: 0 }, tagline: "For trying it out",
    features: ["1 published site", "AI chat builder", "Mobile live preview", "HandsOn subdomain", "Community support"],
    cta: "Start free", highlight: false,
  },
  {
    id: "pro", name: "Pro", price: { m: 19, y: 190 }, tagline: "For freelancers",
    features: ["Unlimited sites", "One-tap Netlify deploy", "Custom domains", "Tap-to-edit refinements", "Priority AI generation", "Remove HandsOn badge"],
    cta: "Go Pro", highlight: true,
  },
  {
    id: "agency", name: "Agency", price: { m: 49, y: 490 }, tagline: "For teams & agencies",
    features: ["Everything in Pro", "Client workspaces", "Team seats", "Bulk deploys", "White-label share links", "Dedicated support"],
    cta: "Choose Agency", highlight: false,
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const selectPlan = async (planId) => {
    if (planId === "free") {
      navigate(user ? "/app" : "/login");
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    const lookupKey = `${planId}_${yearly ? "yearly" : "monthly"}`;
    setLoadingPlan(planId);
    try {
      const { data } = await client.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout. Try again.");
      setLoadingPlan(null);
    }
  };

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 pt-14 pb-4 text-center md:pt-20">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-ho-primary">Pricing</span>
        <h1 className="font-display mx-auto mt-4 max-w-2xl text-4xl font-black tracking-tighter md:text-6xl">
          Simple pricing. Big margins for you.
        </h1>
        <p className="mx-auto mt-5 max-w-lg text-lg text-ho-muted">
          Start free. Upgrade when you're ready to ship more. Cancel anytime.
        </p>

        <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-black/5 bg-white p-1">
          <button onClick={() => setYearly(false)} data-testid="billing-monthly"
            className={`tap-scale rounded-full px-5 py-2 text-sm font-semibold ${!yearly ? "bg-ho-ink text-white" : "text-ho-muted"}`}>
            Monthly
          </button>
          <button onClick={() => setYearly(true)} data-testid="billing-yearly"
            className={`tap-scale rounded-full px-5 py-2 text-sm font-semibold ${yearly ? "bg-ho-ink text-white" : "text-ho-muted"}`}>
            Yearly <span className="text-ho-primary">−17%</span>
          </button>
        </div>

        {!yearly && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="mx-auto mt-4 flex w-fit items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700"
            data-testid="annual-nudge-banner">
            <TrendingDown className="h-4 w-4" /> Switch to yearly and get 2 months free
          </motion.div>
        )}
      </section>

      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid gap-4 md:grid-cols-3">
          {PLANS.map((p, i) => (
            <motion.div key={p.id}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.45 }}
              className={`relative rounded-[32px] border p-7 ${p.highlight ? "border-ho-primary bg-ho-ink text-white shadow-[0_20px_60px_-20px_rgba(0,85,255,0.5)]" : "border-black/5 bg-white"}`}>
              {p.highlight && (
                <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-ho-primary px-3 py-1 text-xs font-bold text-white">
                  <Sparkles className="w-3 h-3" /> Most popular
                </span>
              )}
              <div className={`text-sm font-medium ${p.highlight ? "text-white/60" : "text-ho-muted"}`}>{p.tagline}</div>
              <h3 className="font-display mt-1 text-2xl font-bold">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-5xl font-black tracking-tighter">${yearly ? p.price.y : p.price.m}</span>
                <span className={`mb-2 text-sm ${p.highlight ? "text-white/50" : "text-ho-muted"}`}>/{yearly ? "yr" : "mo"}</span>
              </div>
              {p.price.m > 0 && (
                yearly ? (
                  <div className="mt-1 text-xs font-bold text-emerald-500">
                    {`That's just $${(p.price.y / 12).toFixed(0)}/mo — 2 months free`}
                  </div>
                ) : (
                  <button onClick={() => setYearly(true)} data-testid={`annual-nudge-${p.id}`}
                    className={`mt-1 inline-flex items-center gap-1 text-xs font-bold ${p.highlight ? "text-emerald-300" : "text-emerald-600"}`}>
                    <TrendingDown className="w-3.5 h-3.5" /> Save ${p.price.m * 12 - p.price.y}/yr with annual
                  </button>
                )
              )}
              <button onClick={() => selectPlan(p.id)} disabled={loadingPlan === p.id} data-testid={`plan-${p.id}-cta`}
                className={`tap-scale mt-6 w-full rounded-full py-3.5 text-sm font-semibold disabled:opacity-60 ${p.highlight ? "bg-white text-ho-primary" : "bg-ho-ink text-white"}`}>
                {loadingPlan === p.id ? "Starting…" : p.cta}
              </button>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[15px]">
                    <Check className={`mt-0.5 w-4 h-4 shrink-0 ${p.highlight ? "text-ho-primary" : "text-ho-primary"}`} />
                    <span className={p.highlight ? "text-white/85" : "text-ho-ink/80"}>{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-ho-muted">
          All plans include the full mobile builder. Secure checkout by Stripe · cancel anytime.
        </p>
      </section>
    </MarketingLayout>
  );
}
